"use client";

import { mergeProps } from "@base-ui/react/merge-props";
import { useRender } from "@base-ui/react/use-render";

import { cx } from "../../cx";
import styles from "./badge.module.css";

export type BadgeVariant =
  | "default"
  | "secondary"
  | "destructive"
  | "outline"
  | "soft"
  | "soft-success"
  | "soft-warning"
  | "soft-danger"
  | "soft-info"
  | "purple"
  | "tag"
  | "neutral";
export type BadgeSize = "xs" | "default" | "lg" | "pill" | "count";

type BadgeProps = Omit<useRender.ComponentProps<"span">, "className"> & {
  className?: string;
  variant?: BadgeVariant;
  size?: BadgeSize;
  numeric?: boolean;
  /** A leading status dot in the badge's own colour. */
  dot?: boolean;
};

function Badge({
  className,
  variant = "default",
  size = "default",
  dot = false,
  numeric = false,
  render,
  children,
  ...props
}: BadgeProps) {
  const content = dot ? (
    <>
      <span className={styles.dot} aria-hidden />
      {children}
    </>
  ) : (
    children
  );

  const defaultProps = {
    className: cx(styles.badge, className),
    "data-tui": "",
    "data-slot": "badge",
    "data-variant": variant,
    "data-size": size,
    "data-numeric": numeric ? "" : undefined,
  };

  const consumerProps: Record<string, unknown> = { ...props };
  if (content !== undefined) consumerProps.children = content;

  return useRender({
    defaultTagName: "span",
    props: mergeProps<"span">(defaultProps, consumerProps),
    render,
  });
}

export { Badge };
